// src/utils/emulation.js
// User emulation utility for admins to browse the app as another user
import { clearLocationCache } from './locationApi';

const EMULATION_KEYS = {
  IS_EMULATING: 'isEmulating',
  EMULATED_USER: 'emulatedUser',
  ORIGINAL_USER_SETTINGS: 'originalUserSettings',
  ORIGINAL_ORG_CODE: 'originalOrgCode',
  ORIGINAL_ORG_ID: 'originalOrgId',
  ORIGINAL_ORG_WORKSPACE_ID: 'originalOrgWorkspaceId',
};

/**
 * Check if currently emulating another user
 */
export const isEmulating = () => {
  return localStorage.getItem(EMULATION_KEYS.IS_EMULATING) === 'true';
};

/**
 * Get the emulated user's data
 */
export const getEmulatedUser = () => {
  const emulatedUser = localStorage.getItem(EMULATION_KEYS.EMULATED_USER);
  return emulatedUser ? JSON.parse(emulatedUser) : null;
};

/**
 * Get the original admin user's settings (stored before emulation started)
 */
export const getOriginalUserSettings = () => {
  const original = localStorage.getItem(EMULATION_KEYS.ORIGINAL_USER_SETTINGS);
  return original ? JSON.parse(original) : null;
};

/**
 * Start emulating a user
 * @param {Object} targetUser - The user to emulate (full user object from admin panel)
 * @param {Object} currentUserSettings - Current admin's user settings to preserve
 */
export const startEmulation = (targetUser, currentUserSettings) => {
  if (!targetUser || !targetUser.email) {
    console.error('[Emulation] Cannot start emulation: target user is invalid');
    return false;
  }

  if (!currentUserSettings) {
    console.error('[Emulation] Cannot start emulation: current user settings not provided');
    return false;
  }

  console.log('[Emulation] Starting emulation for user:', targetUser.email);

  // Store the original admin's user settings
  localStorage.setItem(EMULATION_KEYS.ORIGINAL_USER_SETTINGS, JSON.stringify(currentUserSettings));

  // Store original org context
  const currentOrgCode = localStorage.getItem('currentOrgCode');
  const currentOrgId = localStorage.getItem('currentOrgId');
  const currentOrgWorkspaceId = localStorage.getItem('currentOrgWorkspaceId');

  if (currentOrgCode) localStorage.setItem(EMULATION_KEYS.ORIGINAL_ORG_CODE, currentOrgCode);
  if (currentOrgId) localStorage.setItem(EMULATION_KEYS.ORIGINAL_ORG_ID, currentOrgId);
  if (currentOrgWorkspaceId) localStorage.setItem(EMULATION_KEYS.ORIGINAL_ORG_WORKSPACE_ID, currentOrgWorkspaceId);

  // Ensure the emulated user has required fields with defaults
  const normalizedUser = {
    ...targetUser,
    isAdmin: targetUser.isAdmin || 0,
    orgAccess: targetUser.orgAccess || {},
  };

  // Normalize orgAccess structure
  if (normalizedUser.orgAccess) {
    Object.keys(normalizedUser.orgAccess).forEach(orgCode => {
      const orgAccessData = normalizedUser.orgAccess[orgCode];
      normalizedUser.orgAccess[orgCode] = {
        reportIds: orgAccessData.reportIds || {},
        dashboardIds: orgAccessData.dashboardIds || [],
        locationIds: orgAccessData.locationIds || [],
        departmentIds: orgAccessData.departmentIds || [-1],
        chartOfAccountDepartmentIds: orgAccessData.chartOfAccountDepartmentIds || [-1],
        dashboards: orgAccessData.dashboards || [],
        reports: orgAccessData.reports || [],
        // PBI report grants mapped to native report groups (server-stamped).
        nativeReports: Array.isArray(orgAccessData.nativeReports) ? orgAccessData.nativeReports : null,
        role: orgAccessData.role || '',
      };
    });
  }

  // Store the emulated user
  localStorage.setItem(EMULATION_KEYS.EMULATED_USER, JSON.stringify(normalizedUser));

  // Set the emulated user's settings as the current user settings
  localStorage.setItem('userSettings', JSON.stringify(normalizedUser));

  // Set the emulated user's first organization as current (if they have any)
  if (normalizedUser.orgAccess && Object.keys(normalizedUser.orgAccess).length > 0) {
    const firstOrgCode = Object.keys(normalizedUser.orgAccess)[0];
    localStorage.setItem('currentOrgCode', firstOrgCode);
    // Clear org ID and workspace ID - they'll be set when the page reloads
    localStorage.removeItem('currentOrgId');
    localStorage.removeItem('currentOrgWorkspaceId');
  }

  // Set emulation flag
  localStorage.setItem(EMULATION_KEYS.IS_EMULATING, 'true');

  // Clear location cache so emulated user gets fresh location data
  clearLocationCache();

  console.log('[Emulation] Emulation started successfully. User orgAccess:', Object.keys(normalizedUser.orgAccess || {}));
  return true;
};

/**
 * Stop emulating and restore original admin user
 */
export const stopEmulation = () => {
  // Restore original admin user settings
  const originalSettings = localStorage.getItem(EMULATION_KEYS.ORIGINAL_USER_SETTINGS);
  if (originalSettings) {
    localStorage.setItem('userSettings', originalSettings);
  }

  // Restore original org context
  const originalOrgCode = localStorage.getItem(EMULATION_KEYS.ORIGINAL_ORG_CODE);
  const originalOrgId = localStorage.getItem(EMULATION_KEYS.ORIGINAL_ORG_ID);
  const originalOrgWorkspaceId = localStorage.getItem(EMULATION_KEYS.ORIGINAL_ORG_WORKSPACE_ID);

  if (originalOrgCode) {
    localStorage.setItem('currentOrgCode', originalOrgCode);
  }
  if (originalOrgId) {
    localStorage.setItem('currentOrgId', originalOrgId);
  }
  if (originalOrgWorkspaceId) {
    localStorage.setItem('currentOrgWorkspaceId', originalOrgWorkspaceId);
  }

  // Clear location cache so admin gets fresh location data after stopping emulation
  clearLocationCache();

  // Clean up emulation-related localStorage keys
  localStorage.removeItem(EMULATION_KEYS.IS_EMULATING);
  localStorage.removeItem(EMULATION_KEYS.EMULATED_USER);
  localStorage.removeItem(EMULATION_KEYS.ORIGINAL_USER_SETTINGS);
  localStorage.removeItem(EMULATION_KEYS.ORIGINAL_ORG_CODE);
  localStorage.removeItem(EMULATION_KEYS.ORIGINAL_ORG_ID);
  localStorage.removeItem(EMULATION_KEYS.ORIGINAL_ORG_WORKSPACE_ID);
};

/**
 * Get the user ID and email to use for API requests
 * Returns emulated user info if emulating, otherwise null (use JWT extraction)
 */
export const getEmulatedUserForApi = () => {
  if (!isEmulating()) {
    return null;
  }

  const emulatedUser = getEmulatedUser();
  if (!emulatedUser) {
    return null;
  }

  return {
    userId: emulatedUser.cognitoId || emulatedUser.email,
    userEmail: emulatedUser.email,
  };
};

export default {
  isEmulating,
  getEmulatedUser,
  getOriginalUserSettings,
  startEmulation,
  stopEmulation,
  getEmulatedUserForApi,
};
